import React, { useEffect, useRef, useState } from "react";
import {
  ArrowLeft,
  User,
  Lock,
  Brain,
  Upload,
  HelpCircle,
  Settings as SettingsIcon,
  Bell,
  Volume2,
  VolumeX,
  ToggleLeft,
  ToggleRight,
  FileText,
  BookOpen,
  Target,
  BarChart3,
  RefreshCw,
  Zap,
  LogIn,
  LogOut,
  EyeOff,
  Eye,
  Globe,
} from "lucide-react";
import { useTranslation } from 'react-i18next';
import { LanguageSelector } from './LanguageSelector';
import { Button } from "./ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "./ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";
import { Switch } from "./ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { useAuth } from "../contexts/AuthContext";
import useUserStore from "@/stores/userStore";
import AuthModal from "./auth/AuthModal";
import PDFUploadModal from "./pdf/PDFUploadModal";
import api, { profileAPI } from "@/services/api";
import {
  clearDeletionState,
  getCurrentAccessToken,
  getDeletionIdempotencyKey,
} from "@/services/accountDeletion";
import AvatarUpload from "./AvatarUpload";
import { openCookiePreferences } from "../services/cookiePreferences";


const SettingsScreen = ({ onBack, currentUser }) => {
  const { t, i18n } = useTranslation();
  const { user, logout, isAuthenticated } = useAuth();
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [pdfUploadModalOpen, setPdfUploadModalOpen] = useState(false);
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteStep, setDeleteStep] = useState("confirm");
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const [deleteConfirmation, setDeleteConfirmation] = useState("");
  const [deletionReceipt, setDeletionReceipt] = useState(null);
  const deleteErrorRef = useRef(null);

  const [bugModalOpen, setBugModalOpen] = useState(false);
  const fileInputRef = useRef(null);
  const [bugData, setBugData] = useState({
    title: "",
    description: "",
    screenshot: null,
  });
  const [isSubmittingBug, setIsSubmittingBug] = useState(false);
  const [bugError, setBugError] = useState("");
  const [bugSuccess, setBugSuccess] = useState(false);

  const handleBugSubmit = async () => {
    if (!bugData.title || !bugData.description) {
      setBugError(t('settings.bug_report_validation_error'));
      return;
    }

    setIsSubmittingBug(true);
    setBugError("");
    setBugSuccess(false);

    try {
      const response = await api.post("/auth/report-bug", bugData); // using your api.js
      if (response?.data.success) {
        setBugSuccess(true);
        fileInputRef.current.value = null;
        setBugData({ title: "", description: "", screenshot: null });

      } else {
        console.log(response);
        setBugError(t('settings.bug_report_send_failed'));
      }
    } catch (err) {
      console.error(err);
      setBugError(t('settings.bug_report_error'));
    } finally {
      setIsSubmittingBug(false);
    }
  };

  // Initialize preferences from user profile or use defaults
  const [userProfile, setUserProfile] = useState({
    username: user?.username || "User",
    email: user?.email || "No email",
    full_name: user?.full_name || "Demo User",
    avatar_url: user?.avatar_url,
    preferences: user?.preferences || {
      soundEnabled: true,
      notificationsEnabled: true,
      autoplayEnabled: false,
      learningMode: "narrative",
      difficultyLevel: "normal",
      reverseQuizMode: true,
    },
  });

  const [isUpdating, setIsUpdating] = useState(false);
  const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);

  // Extract preferences for easier access
  const preferences = userProfile.preferences || {};
  const {
    soundEnabled = true,
    notificationsEnabled = true,
    autoplayEnabled = false,
    learningMode = "narrative",
    difficultyLevel = "normal",
    reverseQuizMode = true,
  } = preferences;

  useEffect(() => {
    if (isAuthenticated && user) {
      loadUserProfile();
    }
  }, [isAuthenticated, user]);

  useEffect(() => {
    if (!deleteError || !deleteErrorRef.current) return;
    deleteErrorRef.current.focus();
  }, [deleteError]);

  const resetDeletionDialog = () => {
    setDeleteStep("confirm");
    setDeleteError("");
    setDeleteConfirmation("");
    setDeletionReceipt(null);
  };

  const handleDeleteDialogChange = (open) => {
    if (!open && isDeletingAccount) return;
    setShowDeleteDialog(open);
    if (!open) resetDeletionDialog();
  };

  const handleOpenDeleteDialog = () => {
    resetDeletionDialog();
    setShowDeleteDialog(true);
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmation !== "DELETE" || isDeletingAccount) return;
    setIsDeletingAccount(true);
    setDeleteError("");
    try {
      const token = await getCurrentAccessToken();
      const response = await api.delete("/auth/account", {
        headers: {
          Authorization: `Bearer ${token}`,
          "Idempotency-Key": getDeletionIdempotencyKey(),
          "X-Deletion-Confirmation": "DELETE",
        },
      });
      if (response?.data?.success) {
        setDeletionReceipt({ completedAt: new Date().toISOString() });
        setDeleteStep("success");
      } else {
        setDeleteError(t("settings.delete_failed"));
      }
    } catch (err) {
      setDeleteError(err?.response?.data?.error || t("settings.delete_failed"));
    } finally {
      setIsDeletingAccount(false);
    }
  };

  const handleFinishDeletion = async () => {
    clearDeletionState();
    await logout();
  };
  const loadUserProfile = async () => {
    try {
      const response = await profileAPI.getProfile();
      if (response) {
        console.log("User profile loaded:", response);

        const updatedProfile = {
          username: response.user.username || user?.username || "User",
          email: response.user.email || user?.email || "No email",
          avatar_url: response.user.avatar_url,
          full_name: response.user.full_name || user?.full_name || "Demo User",
          bio: response.user.bio || user?.bio || "",
          preferences: response.user.preferences || {
            soundEnabled: true,
            notificationsEnabled: true,
            autoplayEnabled: false,
            learningMode: "narrative",
            difficultyLevel: "normal",
            reverseQuizMode: true,
          },
        };

        setUserProfile(updatedProfile);

        // Update global user store
        const setUser = useUserStore.getState().setUser;
        setUser(response.user);

        // Sync language from profile preferences
        if (response.user.preferences?.language) {
          const savedLang = response.user.preferences.language;
          if (savedLang !== i18n.language) {
            console.log(`🌍 Syncing language from profile: ${savedLang}`);
            i18n.changeLanguage(savedLang);
          }
        }
      }
    } catch (error) {
      console.error("Failed to load user profile:", error);
    }
  };

  // Update preference in database
  const updatePreference = async (preferenceKey, value) => {
    if (!isAuthenticated) {
      alert(t('settings.preferences_auth_required'));
      return;
    }

    setIsUpdating(true);
    try {
      // Create deep copy of preferences to avoid mutation issues
      const currentPrefs = userProfile.preferences || {};
      const updatedPreferences = {
        ...currentPrefs,
        [preferenceKey]: value,
      };

      const updateData = {
        preferences: updatedPreferences,
      };

      console.log('🔄 Saving preference:', preferenceKey, value);
      const response = await profileAPI.updateProfile(updateData);

      if (response && (response.success || response.profile)) {
        setUserProfile((prev) => ({
          ...prev,
          preferences: updatedPreferences,
        }));
        
        // Also update global store
        const user = useUserStore.getState().user;
        if (user) {
          useUserStore.getState().setUser({
            ...user,
            preferences: updatedPreferences
          });
        }

        console.log(
          "✅ Preference updated successfully:",
          preferenceKey,
          value
        );
      } else {
        throw new Error("API response did not indicate success");
      }
    } catch (error) {
      console.error("❌ Failed to update preference:", error);
      // Log detailed error from axios if available
      if (error.response) {
        console.error("Server Response:", error.response.data);
      }
      alert(`Failed to update preference: ${error.message || "Unknown error"}`);
    } finally {
      setIsUpdating(false);
    }
  };

  // Handler functions for preferences
  const handleSoundEnabledChange = (value) => {
    updatePreference("soundEnabled", value);
  };

  const handleLanguageChange = (value) => {
    i18n.changeLanguage(value);
    updatePreference("language", value);
  };

  const handleNotificationsEnabledChange = (value) => {
    updatePreference("notificationsEnabled", value);
  };

  const handleAutoplayEnabledChange = (value) => {
    updatePreference("autoplayEnabled", value);
  };

  const handleLearningModeChange = (value) => {
    updatePreference("learningMode", value);
  };

  const handleDifficultyLevelChange = (value) => {
    updatePreference("difficultyLevel", value);
  };

  const handleReverseQuizModeChange = (value) => {
    updatePreference("reverseQuizMode", value);
  };

  const handlePasswordChange = (field, value) => {
    setPasswordData((prev) => ({
      ...prev,
      [field]: value,
    }));
    // Clear error when user starts typing
    if (passwordError) setPasswordError("");
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
  });

  const validatePassword = () => {
    if (!passwordData.currentPassword) {
      setPasswordError("t('settings.current_password_required')");
      return false;
    }

    if (!passwordData.newPassword) {
      setPasswordError("t('settings.new_password_required')");
      return false;
    }

    if (passwordData.newPassword.length < 6) {
      setPasswordError("t('settings.password_min_length')");
      return false;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordError("t('settings.password_mismatch')");
      return false;
    }

    if (passwordData.currentPassword === passwordData.newPassword) {
      setPasswordError("t('settings.password_same_as_current')");
      return false;
    }

    return true;
  };

  const resetPasswordModal = () => {
    setPasswordData({
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    });
    setPasswordError("");
    setShowPasswords({
      current: false,
      new: false,
      confirm: false,
    });
  };

  const handlePasswordUpdate = async () => {
    if (!validatePassword()) return;

    setIsUpdatingPassword(true);
    setPasswordError("");

    try {
      const response = await profileAPI.updatePassword(
        passwordData.currentPassword,
        passwordData.newPassword
      );

      if (response.success) {
        alert(
          t('settings.password_updated_success')
        );
        setPasswordModalOpen(false);
        setPasswordData({
          currentPassword: "",
          newPassword: "",
          confirmPassword: "",
        });

        setTimeout(() => {
          logout();
        }, 1000);
      }
    } catch (error) {
      console.error("❌ Password update error:", error);

      let errorMessage = "t('settings.password_update_failed')";

      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.message.includes("Invalid login credentials")) {
        errorMessage = "t('settings.password_incorrect')";
      } else if (error.message.includes("rate limit")) {
        errorMessage = "t('settings.password_too_many_attempts')";
      }

      setPasswordError(errorMessage);
    } finally {
      setIsUpdatingPassword(false);
    }
  };

  const updateProfileField = async (field, value) => {
    if (!isAuthenticated) {
      alert(t('settings.profile_auth_required'));
      return;
    }

    setIsUpdating(true);
    try {
      const updateData = { [field]: value };
      const response = await profileAPI.updateProfile(updateData);

      if (response.success) {
        setUserProfile((prev) => ({
          ...prev,
          [field]: value,
        }));

        // Update global user store
        if (field === 'avatar_url') {
          const updateAvatar = useUserStore.getState().updateAvatar;
          updateAvatar(value);
        } else if (field === 'bio') {
          const updateBio = useUserStore.getState().updateBio;
          updateBio(value);
        } else {
          const updateUser = useUserStore.getState().updateUser;
          updateUser({ [field]: value });
        }

        console.log("✅ Profile updated successfully");
      }
    } catch (error) {
      console.error("❌ Failed to update profile:", error);
      alert(t('settings.profile_update_failed'));
    } finally {
      setIsUpdating(false);
    }
  };

  // Update settings sections to use the new handlers
  const settingsSections = [
    {
      id: "account",
      title: isAuthenticated ? t('settings.account_section') : t('settings.authentication_section'),
      icon: User,
      items: isAuthenticated
        ? [
            {
              id: "full_name",
              label: t('settings.full_name_label'),
              value: userProfile.full_name,
              type: "input",
              description: t('settings.full_name_description'),
              onSave: (value) => updateProfileField("full_name", value),
            },
            {
              id: "username",
              label: t('auth.username_label') || t('settings.username_label'),
              value: userProfile.username,
              type: "input",
              description: t('settings.username_description'),
              onSave: (value) => updateProfileField("username", value),
            },
            {
              id: "bio",
              label: t('settings.bio_label'),
              value: userProfile.bio || "",
              type: "textarea",
              description: t('settings.bio_description'),
              onSave: (value) => updateProfileField("bio", value),
            },
            {
              id: "email",
              label: t('auth.email_label') || t('settings.email_label'),
              value: user?.email || "No email",
              type: "input",
              description: t('settings.email_description'),
            },
            {
              id: "password",
              label: t('settings.change_password_label'),
              type: "button",
              description: t('settings.change_password_description'),
              action: "change-password",
            },
            {
              id: "logout",
              label: t('auth.logout') || t('settings.sign_out_label'),
              type: "button",
              description: t('settings.sign_out_description'),
              icon: LogOut,
              action: "logout",
            },
          ]
        : [
            {
              id: "login",
              label: t('auth.sign_in_button') || "Sign In / Register",
              type: "button",
              description: "Sign in to your account or create a new one",
              icon: LogIn,
              action: "login",
            },
          ],
    },
    {
      id: "preferences",
      title: t('settings.preferences_section') || "Preferences",
      icon: Globe,
      items: [
        {
          id: "language",
          label: t('settings.language_label') || "Language",
          type: "custom",
          component: (
            <div className="pt-2">
              <LanguageSelector 
                value={preferences.language || i18n.language} 
                onChange={handleLanguageChange}
                variant="default"
                className="w-full"
              />
            </div>
          ),
          description: "Select your preferred language",
        },
        {
          id: "cookie-settings",
          label: t("settings.cookie_settings", { defaultValue: "Cookie settings" }),
          type: "button",
          description: t("settings.cookie_settings_description", {
            defaultValue: "Change or withdraw your optional analytics consent",
          }),
          action: "cookie-settings",
        },
      ],
    },
    {
      id: "help",
      title: t('settings.help_about_section') || "Help & About",
      icon: HelpCircle,
      items: [
        {
          id: "app-info",
          label: t('settings.app_info') || "App Information",
          type: "info",
          value: "ScrollEd v1.0.0",
          description: t('settings.app_description'),
        },
        {
          id: "contact",
          label: t('settings.contact_support') || "Contact Support",
          type: "button",
          description: "Get help with technical issues or feedback",
          action: "support",
        },
        {
          id: "bug-report",
          label: t('settings.report_bug') || t('settings.report_bug_modal_title'),
          type: "button",
          description: "Help us improve ScrollEd by reporting issues",
          action: "report-bug",
        },
        {
          id: "privacy-policy",
          label: t('settings.privacy_policy') || "Privacy Policy",
          type: "button",
          description: "How we collect, use, and protect your data",
          action: "privacy",
        },
        {
          id: "terms-of-service",
          label: t('settings.terms_of_service') || "Terms of Service",
          type: "button",
          description: "Rules and guidelines for using ScrollEd",
          action: "terms",
        },
      ],
    },
  ];

  const renderSettingItem = (item, sectionId) => {
    switch (item.type) {
      case "textarea":
        const [textareaValue, setTextareaValue] = useState(item.value);

        useEffect(() => {
          setTextareaValue(item.value);
        }, [item.value]);

        const handleTextareaSave = () => {
          if (textareaValue !== item.value && item.onSave) {
            item.onSave(textareaValue);
          }
        };

        return (
          <div className="space-y-2.5">
            <Label className="text-sm font-bold text-slate-700">{item.label}</Label>
            <div className="space-y-3">
              <textarea
                value={textareaValue}
                onChange={(e) => setTextareaValue(e.target.value)}
                className="w-full px-4 py-3 border border-slate-200 rounded-xl bg-white text-sm focus:ring-1 focus:ring-blue-500 focus:border-transparent outline-none transition-all resize-none"
                rows={3}
                maxLength={160}
                placeholder={item.label}
                disabled={isUpdating}
              />
              <div className="flex justify-between items-center px-1">
                <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">{textareaValue.length}/160 characters</p>
                <Button size="sm" onClick={handleTextareaSave} disabled={isUpdating || textareaValue === item.value} className="rounded-lg h-8 px-4 font-bold shadow-sm">
                  {isUpdating ? "..." : t('common.save')}
                </Button>
              </div>
            </div>
            {item.description && (
              <p className="text-[11px] font-medium text-slate-400 tracking-tight">{item.description}</p>
            )}
          </div>
        );

      case "input":
        const [inputValue, setInputValue] = useState(item.value);

        useEffect(() => {
          setInputValue(item.value);
        }, [item.value]);

        const handleSave = () => {
          if (inputValue !== item.value && item.onSave) {
            item.onSave(inputValue);
          }
        };

        const handleKeyPress = (e) => {
          if (e.key === "Enter") {
            handleSave();
          }
        };

        return (
          <div className="space-y-2.5">
            <Label className="text-sm font-bold text-slate-700">{item.label}</Label>
            <div className="flex gap-2">
              <Input
                value={inputValue}
                onChange={(e) =>
                  setInputValue(item.id === "username" ? e.target.value.replace(/\s/g, "") : e.target.value)
                }
                onKeyPress={handleKeyPress}
                className="flex-1 h-11 rounded-xl border-slate-200 bg-white px-4 focus:ring-1 focus:ring-blue-500"
                placeholder={item.label}
                disabled={isUpdating || item.id === "email"}
              />
              {item.id !== "email" && inputValue !== item.value && (
                <Button size="sm" onClick={handleSave} disabled={isUpdating} className="h-11 rounded-xl px-5 font-bold shadow-sm">
                  {isUpdating ? "..." : t('common.save')}
                </Button>
              )}
            </div>
            {item.description && (
              <p className="text-[11px] font-medium text-slate-400 tracking-tight px-1">{item.description}</p>
            )}
          </div>
        );

      case "toggle":
        return (
          <div className="flex items-center justify-between py-1">
            <div className="space-y-1">
              <Label className="text-sm font-bold text-slate-700">{item.label}</Label>
              {item.description && (
                <p className="text-[11px] font-medium text-slate-400 tracking-tight">{item.description}</p>
              )}
            </div>
            <div className="flex items-center gap-3">
              {item.id === "sound" &&
                (item.value ? (
                  <Volume2 className="w-5 h-5 text-blue-500" />
                ) : (
                  <VolumeX className="w-5 h-5 text-slate-300" />
                ))}
              <Switch
                checked={item.value}
                onCheckedChange={item.onChange}
                disabled={isUpdating}
                className="data-[state=checked]:bg-blue-600"
              />
              {isUpdating && <RefreshCw className="w-4 h-4 animate-spin text-blue-500" />}
            </div>
          </div>
        );

      case "select":
        const [selectValue, setSelectValue] = useState(item.value);

        useEffect(() => {
          setSelectValue(item.value);
        }, [item.value]);

        const handleSelectChange = (value) => {
          setSelectValue(value);
          if (item.onChange) {
            item.onChange(value);
          }
        };

        return (
          <div className="space-y-2.5">
            <Label className="text-sm font-bold text-slate-700">{item.label}</Label>
            <Select
              value={selectValue}
              onValueChange={handleSelectChange}
              disabled={isUpdating}
            >
              <SelectTrigger className="h-11 rounded-xl border-slate-200 bg-white focus:ring-1 focus:ring-blue-500">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="rounded-xl border-slate-100 shadow-xl">
                {item.options?.map((option) => (
                  <SelectItem key={option.value} value={option.value} className="rounded-lg py-2.5">
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {item.description && (
              <p className="text-[11px] font-medium text-slate-400 tracking-tight">{item.description}</p>
            )}
          </div>
        );

      case "button":
        return (
          <div className="space-y-2">
            <Button
              variant={item.variant || "outline"}
              className={`w-full justify-start h-11 rounded-xl font-semibold border-slate-200 hover:bg-slate-50 transition-all ${
                item.disabled ? "opacity-50 cursor-not-allowed" : ""
              }`}
              disabled={item.disabled}
              onClick={() => {
                if (item.action === "login") {
                  setAuthModalOpen(true);
                } else if (item.action === "logout") {
                  setShowLogoutDialog(true);
                } else if (item.action === "upload-pdf") {
                  setPdfUploadModalOpen(true);
                } else if (item.action === "change-password") {
                  setPasswordModalOpen(true);
                } else if (item.action === "report-bug") {
                  setBugModalOpen(true);
                } else if (item.action === "support") {
                  window.open(
                    "mailto:support@scrolled.eu?subject=Support%20Request%20-%20ScrollEd"
                  );
                } else if (item.action === "cookie-settings") {
                  openCookiePreferences();
                } else if (item.action === "privacy") {
                  window.location.href = "/privacy";
                } else if (item.action === "terms") {
                  window.location.href = "/terms";
                } else if (item.id === "upload-pdf") {
                  if (!isAuthenticated) {
                    alert(t('settings.profile_auth_required'));
                  } else {
                    setPdfUploadModalOpen(true);
                  }
                } else if (item.id === "performance") {
                  alert(t('settings.performance_analytics_coming'));
                } else if (item.id === "reset-history") {
                  if (
                    confirm(t('settings.quiz_history_reset_confirm') || "Are you sure you want to reset all quiz history?")
                  ) {
                    alert(t('settings.quiz_history_reset'));
                  }
                } else {
                  alert(`⚙️ ${item.label} - ${t('settings.setting_coming_soon', { label: '' })}`);
                }
              }}
            >
              {item.icon && <item.icon className="w-4 h-4 mr-2.5 text-blue-500" />}
              {item.label}
            </Button>
            {item.description && (
              <p className="text-[11px] font-medium text-slate-400 dark:text-slate-500 tracking-tight px-1">{item.description}</p>
            )}
          </div>
        );

      case "info":
        return (
          <div className="space-y-2">
            <Label className="text-sm font-bold text-slate-700">{item.label}</Label>
            <div className="p-3 bg-slate-50 rounded-xl border border-slate-100">
              <p className="text-sm font-semibold text-slate-900">{item.value}</p>
              {item.description && (
                <p className="text-[11px] font-medium text-slate-400 mt-1.5">{item.description}</p>
              )}
            </div>
          </div>
        );

      case "custom":
        return (
          <div className="space-y-2.5">
            <Label className="text-sm font-bold text-slate-700">{item.label}</Label>
            {item.component}
            {item.description && (
              <p className="text-[11px] font-medium text-slate-400 tracking-tight px-1">{item.description}</p>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file); // gives "data:image/png;base64,...."
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  };

  const handleAvatarUpdate = (newAvatarUrl) => {
    setUserProfile(prev => ({
      ...prev,
      avatar_url: newAvatarUrl
    }));

    // Update global user store
    const updateAvatar = useUserStore.getState().updateAvatar;
    updateAvatar(newAvatarUrl);
  };

  return (
    <div className="min-h-screen bg-slate-50 transition-colors duration-300">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-md border-b border-slate-200 sticky top-0 z-50">
        <div className="flex items-center justify-between p-4 max-w-2xl mx-auto">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={onBack} className="rounded-full w-10 h-10 p-0 hover:bg-slate-100">
              <ArrowLeft className="w-5 h-5 text-slate-600" />
            </Button>
            <h1 className="font-black text-xl tracking-tight text-slate-900">{t('settings.title')}</h1>
          </div>
          <div className="p-2 bg-blue-500/10 rounded-full">
            <SettingsIcon className="w-5 h-5 text-blue-600" />
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto bg-transparent min-h-screen pb-20">
        <div className="p-4 space-y-8">
          {/* User Profile Section */}

               <AvatarUpload 
        userProfile={userProfile} 
        onAvatarUpdate={handleAvatarUpdate}
      />



          {/* Settings Sections */}
          <div className="space-y-6">
            {settingsSections.map((section) => (
              <div key={section.id} className="space-y-3">
                <div className="flex items-center gap-2 px-1">
                  <div className="p-1.5 rounded-lg bg-blue-500/10 text-blue-600 dark:text-blue-400">
                    <section.icon className="w-4 h-4" />
                  </div>
                  <h2 className="text-sm font-bold uppercase tracking-wider text-slate-500">
                    {section.title}
                  </h2>
                </div>
                
                <Card className="border-slate-200 shadow-sm overflow-hidden rounded-2xl">
                  <CardContent className="p-0 divide-y divide-slate-100">
                    {section.items.map((item) => (
                      <div key={item.id} className="p-4 hover:bg-slate-50/50 transition-colors">
                        {renderSettingItem(item, section.id)}
                      </div>
                    ))}
                  </CardContent>
                </Card>
              </div>
            ))}
          </div>

          {/* Footer */}
          <div className="text-center py-6">
            <p className="text-xs text-gray-500">ScrollEd v1.0.0</p>
            <p className="text-xs text-gray-400 mt-1">
              Made with ❤️ for learners everywhere
            </p>
          </div>
        </div>
      </div>

      {/* Password Reset Modal */}
      {passwordModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Change Password</h3>

            {passwordError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-800">{passwordError}</p>
              </div>
            )}

            <div className="space-y-4">
              {/* Current Password */}
              <div>
                <Label
                  htmlFor="current-password"
                  className="text-sm font-medium"
                >
                  Current Password
                </Label>
                <div className="relative mt-1">
                  <Input
                    id="current-password"
                    type={showPasswords.current ? "text" : "password"}
                    value={passwordData.currentPassword}
                    onChange={(e) =>
                      handlePasswordChange("currentPassword", e.target.value)
                    }
                    className="w-full pr-10"
                    placeholder={t('settings.current_password_placeholder')}
                    disabled={isUpdatingPassword}
                  />
                  <button
                    type="button"
                    onClick={() => togglePasswordVisibility("current")}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    disabled={isUpdatingPassword}
                  >
                    {showPasswords.current ? (
                      <EyeOff className="w-4 h-4" />
                    ) : (
                      <Eye className="w-4 h-4" />
                    )}
                  </button>
                </div>
              </div>

              {/* New Password */}
              <div>
                <Label htmlFor="new-password" className="text-sm font-medium">
                  New Password
                </Label>
                <div className="relative mt-1">
                  <Input
                    id="new-password"
                    type={showPasswords.new ? "text" : "password"}
                    value={passwordData.newPassword}
                    onChange={(e) =>
                      handlePasswordChange("newPassword", e.target.value)
                    }
                    className="w-full pr-10"
                    placeholder={t('settings.new_password_placeholder')}
                    disabled={isUpdatingPassword}
                  />
                  <button
                    type="button"
                    onClick={() => togglePasswordVisibility("new")}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    disabled={isUpdatingPassword}
                  >
                    {showPasswords.new ? (
                      <EyeOff className="w-4 h-4" />
                    ) : (
                      <Eye className="w-4 h-4" />
                    )}
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Must be at least 6 characters long
                </p>
              </div>

              {/* Confirm Password */}
              <div>
                <Label
                  htmlFor="confirm-password"
                  className="text-sm font-medium"
                >
                  Confirm New Password
                </Label>
                <div className="relative mt-1">
                  <Input
                    id="confirm-password"
                    type={showPasswords.confirm ? "text" : "password"}
                    value={passwordData.confirmPassword}
                    onChange={(e) =>
                      handlePasswordChange("confirmPassword", e.target.value)
                    }
                    className="w-full pr-10"
                    placeholder={t('settings.confirm_new_password_placeholder')}
                    disabled={isUpdatingPassword}
                  />
                  <button
                    type="button"
                    onClick={() => togglePasswordVisibility("confirm")}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    disabled={isUpdatingPassword}
                  >
                    {showPasswords.confirm ? (
                      <EyeOff className="w-4 h-4" />
                    ) : (
                      <Eye className="w-4 h-4" />
                    )}
                  </button>
                </div>
              </div>

              <div className="flex gap-3 justify-end pt-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    setPasswordModalOpen(false);
                    resetPasswordModal();
                  }}
                  disabled={isUpdatingPassword}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handlePasswordUpdate}
                  disabled={isUpdatingPassword}
                >
                  {isUpdatingPassword ? "Updating..." : "Update Password"}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {bugModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md shadow-lg">
            <h3 className="text-lg font-semibold mb-4">Report a Bug</h3>
            <p className="text-sm text-gray-600 mb-4">
              Help us improve ScrollEd by reporting issues you encounter.
            </p>

            {bugError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-800">{bugError}</p>
              </div>
            )}

            {bugSuccess && (
              <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-sm text-green-800">
                  Thanks for your feedback!
                </p>
              </div>
            )}

            <div className="space-y-4">
              {/* Bug Title */}
              <div>
                <Label htmlFor="bug-title" className="text-sm font-medium">
                  Issue Title
                </Label>
                <Input
                  id="bug-title"
                  type="text"
                  value={bugData.title}
                  onChange={(e) =>
                    setBugData({ ...bugData, title: e.target.value })
                  }
                  placeholder={t('settings.issue_title_placeholder')}
                  className="mt-1 w-full"
                  disabled={isSubmittingBug}
                />
              </div>

              {/* Description */}
              <div>
                <Label
                  htmlFor="bug-description"
                  className="text-sm font-medium"
                >
                  Description
                </Label>
                <textarea
                  id="bug-description"
                  value={bugData.description}
                  onChange={(e) =>
                    setBugData({ ...bugData, description: e.target.value })
                  }
                  className="w-full mt-1 border rounded-md p-2 text-sm h-24 resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder={t('settings.description_placeholder')}
                  disabled={isSubmittingBug}
                />
              </div>

              {/* Optional Screenshot Upload */}
              <div>
                <Label htmlFor="bug-screenshot" className="text-sm font-medium">
                  Screenshot (optional)
                </Label>
                <Input
                  id="bug-screenshot"
                  type="file"
                  accept="image/*"
                  ref={fileInputRef}
                  onChange={async (e) => {
                    const file = e.target.files[0];
            
                    if (!file) return;

                    try {
                      const base64 = await fileToBase64(file);
                      setBugData({ ...bugData, screenshot: base64 });
                    } catch (err) {
                      console.error("Failed to read screenshot file:", err);
                      setBugError("Failed to read screenshot file");
                    }
                  }}
                  className="mt-1 w-full"
                  disabled={isSubmittingBug}
                />
              </div>

              <div className="flex gap-3 justify-end pt-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    setBugModalOpen(false);
                    setBugData({
                      title: "",
                      description: "",
                      screenshot: null,
                    });
                    setBugError("");
                    setBugSuccess(false);
                  }}
                  disabled={isSubmittingBug}
                >
                  Cancel
                </Button>
                <Button onClick={handleBugSubmit} disabled={isSubmittingBug}>
                  {isSubmittingBug ? "Submitting..." : "Submit Bug"}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modals */}
      <AuthModal
        isOpen={authModalOpen}
        onClose={() => setAuthModalOpen(false)}
      />
      <PDFUploadModal
        isOpen={pdfUploadModalOpen}
        onClose={() => setPdfUploadModalOpen(false)}
        onUploadSuccess={(result) => {
          console.log("✅ PDF uploaded successfully:", result);
          alert(
            `✅ PDF uploaded! Instance "${result.title}" is being processed.`
          );
        }}
      />
      {/* GDPR Art. 17 - Delete Account */}
      {isAuthenticated && (
        <div className="mx-4 mb-4 mt-2">
          <button
            onClick={handleOpenDeleteDialog}
            className="w-full py-3 rounded-xl border border-red-500/30 text-red-400 text-sm font-semibold hover:bg-red-500/10 transition-all"
          >
            {t("settings.delete_account_button")}
          </button>
        </div>
      )}
      {/* Delete Account Confirmation Dialog — GDPR Art. 17 */}
      <AlertDialog open={showDeleteDialog} onOpenChange={handleDeleteDialogChange}>
        <AlertDialogContent className="max-w-[380px] rounded-3xl border-none shadow-2xl bg-white p-6">
          <AlertDialogHeader className="space-y-3">
            <AlertDialogTitle className="text-2xl font-black tracking-tight text-red-600">
              {deleteStep === "success"
                ? t("settings.delete_complete_title")
                : t("settings.delete_account_title")}
            </AlertDialogTitle>
            <AlertDialogDescription className="sr-only">
              {deleteStep === "confirm"
                ? t("settings.delete_type_description")
                : t("settings.delete_complete_description")}
            </AlertDialogDescription>
            {deleteStep === "confirm" && (
              <div className="space-y-3">
                <p className="text-sm text-slate-600">{t("settings.delete_account_description")}</p>
                <p className="text-sm text-slate-500">{t("settings.delete_account_support")}</p>
                <p className="text-sm text-slate-600">{t("settings.delete_type_description")}</p>
                <Label htmlFor="delete-account-confirmation">{t("settings.delete_type_label")}</Label>
                <Input
                  id="delete-account-confirmation"
                  value={deleteConfirmation}
                  onChange={(event) => setDeleteConfirmation(event.target.value)}
                  autoComplete="off"
                  disabled={isDeletingAccount}
                />
                {isDeletingAccount && (
                  <p className="text-sm font-semibold text-slate-700" role="status" aria-live="polite">
                    {t("settings.delete_in_progress")}
                  </p>
                )}
              </div>
            )}
            {deleteStep === "success" && (
              <div className="space-y-3" role="status" aria-live="polite">
                <p className="text-sm text-slate-700">{t("settings.delete_complete_description")}</p>
                <p className="text-xs text-slate-500">
                  {t("settings.delete_completed_at", {
                    time: deletionReceipt?.completedAt
                      ? new Date(deletionReceipt.completedAt).toLocaleString()
                      : "",
                  })}
                </p>
              </div>
            )}
            {deleteError && (
              <div
                ref={deleteErrorRef}
                tabIndex={-1}
                role="alert"
                aria-live="assertive"
                className="text-sm text-red-600 font-medium space-y-1"
              >
                <p>{deleteError}</p>
                <a className="underline" href="mailto:support@scrolled.eu">
                  {t("settings.delete_contact_support")}
                </a>
              </div>
            )}
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-row gap-3 mt-6 sm:justify-end">
            {deleteStep !== "success" && (
              <AlertDialogCancel
                className="flex-1 mt-0 rounded-xl h-12 font-bold border-slate-200 hover:bg-slate-50 transition-all"
                disabled={isDeletingAccount}
              >
                {t("common.cancel")}
              </AlertDialogCancel>
            )}
            {deleteStep === "confirm" && (
              <AlertDialogAction
                onClick={(event) => {
                  event.preventDefault();
                  handleDeleteAccount();
                }}
                disabled={deleteConfirmation !== "DELETE" || isDeletingAccount}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white rounded-xl h-12 font-bold shadow-lg"
              >
                {isDeletingAccount
                  ? t("settings.delete_in_progress")
                  : t("settings.delete_permanently")}
              </AlertDialogAction>
            )}
            {deleteStep === "success" && (
              <AlertDialogAction
                onClick={(event) => {
                  event.preventDefault();
                  handleFinishDeletion();
                }}
                className="w-full bg-slate-900 hover:bg-slate-800 text-white rounded-xl h-12 font-bold"
              >
                {t("settings.delete_sign_out")}
              </AlertDialogAction>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>



      {/* Logout Confirmation Dialog */}
      <AlertDialog open={showLogoutDialog} onOpenChange={setShowLogoutDialog}>
        <AlertDialogContent className="max-w-[380px] rounded-3xl border-none shadow-2xl bg-white p-6">
          <AlertDialogHeader className="space-y-3">
            <div className="w-12 h-12 bg-red-100 rounded-2xl flex items-center justify-center text-red-600 mb-2">
              <LogOut className="w-6 h-6" />
            </div>
            <AlertDialogTitle className="text-2xl font-black tracking-tight">{t('settings.sign_out_confirm_title') || 'Sign Out?'}</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-500 font-medium">
              {t('settings.sign_out_confirm_desc') || 'Are you sure you want to sign out of your account?'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-row gap-3 mt-6 sm:justify-end">
            <AlertDialogCancel className="flex-1 mt-0 rounded-xl h-12 font-bold border-slate-200 hover:bg-slate-50 transition-all">
              {t('common.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setShowLogoutDialog(false);
                logout();
              }}
              className="flex-1 bg-red-500 hover:bg-red-600 text-white rounded-xl h-12 font-bold shadow-lg shadow-red-500/20 transition-all"
            >
              {t('settings.sign_out_label') || 'Sign Out'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default SettingsScreen;

